/**
 * App — route definitions.
 *
 * Phase 25: route-based code-splitting. Every page is loaded lazily via
 * React.lazy() so it ships as its own JS chunk, fetched on demand when the
 * user navigates to that route. This shrinks the initial download — users no
 * longer pay for the Gantt chart, EVM views, reports, etc. up front.
 *
 * Mechanics:
 *  - Pages use *named* exports; React.lazy() expects a module with a *default*
 *    export. The `lazyNamed` helper bridges that.
 *  - <Suspense> provides the loading fallback shown while a chunk is fetched.
 *  - <ChunkErrorBoundary> catches chunk-load failures (network blips, and the
 *    stale-deploy case where old chunk hashes 404 after a redeploy).
 *
 * The shell (AuthProvider, AuthGate, AppShell) and the two small fallback
 * components stay eagerly imported — they're needed immediately.
 */

import { Suspense, lazy, type ComponentType } from 'react';
import { Routes, Route } from 'react-router-dom';
import { AppShell } from '@/components/layout/AppShell';
import { ToastViewport } from '@/components/ui/ToastViewport';
import { AuthProvider } from '@/components/auth/AuthProvider';
import { AuthGate } from '@/components/auth/AuthGate';
import { RouteFallback } from '@/components/layout/RouteFallback';
import { ChunkErrorBoundary } from '@/components/layout/ChunkErrorBoundary';
import { UpdateRequiredGate } from '@/components/layout/UpdateRequiredGate';

/**
 * Wrap a dynamic import of a module with a NAMED export so it satisfies
 * React.lazy()'s requirement of a `default` export.
 *
 * The generic P preserves the wrapped component's prop types, so callers
 * like <ComingSoonPage title="..." /> still typecheck.
 */
function lazyNamed<P extends object = Record<string, never>>(
  importer: () => Promise<Record<string, unknown>>,
  exportName: string,
) {
  return lazy(async () => {
    const mod = await importer();
    return { default: mod[exportName] as ComponentType<P> };
  });
}

// --- Auth pages (full-screen, no AppShell) ---
const SignupPage = lazyNamed(() => import('@/pages/auth/SignupPage'), 'SignupPage');
const LoginPage = lazyNamed(() => import('@/pages/auth/LoginPage'), 'LoginPage');
const ForgotPasswordPage = lazyNamed(
  () => import('@/pages/auth/ForgotPasswordPage'),
  'ForgotPasswordPage',
);
const VerifyEmailPage = lazyNamed(() => import('@/pages/auth/VerifyEmailPage'), 'VerifyEmailPage');
const AcceptInvitePage = lazyNamed(
  () => import('@/pages/auth/AcceptInvitePage'),
  'AcceptInvitePage',
);

// --- Application pages ---
const HomePage = lazyNamed(() => import('@/pages/HomePage'), 'HomePage');
const ProjectsPage = lazyNamed(() => import('@/pages/ProjectsPage'), 'ProjectsPage');
const IntakePage = lazyNamed(() => import('@/pages/IntakePage'), 'IntakePage');
const GuidePage = lazyNamed(() => import('@/pages/GuidePage'), 'GuidePage');
const WhatsNewPage = lazyNamed(() => import('@/pages/WhatsNewPage'), 'WhatsNewPage');
const LessonsPage = lazyNamed(() => import('@/pages/LessonsPage'), 'LessonsPage');
const BriefingPage = lazyNamed(() => import('@/pages/BriefingPage'), 'BriefingPage');
const ProjectDetailPage = lazyNamed(
  () => import('@/pages/ProjectDetailPage'),
  'ProjectDetailPage',
);
const ReportsHub = lazyNamed(() => import('@/pages/ReportsHub'), 'ReportsHub');
const PortfolioStatusReport = lazyNamed(
  () => import('@/pages/reports/PortfolioStatusReport'),
  'PortfolioStatusReport',
);
const BenefitsRealizationReport = lazyNamed(
  () => import('@/pages/reports/BenefitsRealizationReport'),
  'BenefitsRealizationReport',
);
const ProjectStatusReport = lazyNamed(
  () => import('@/pages/reports/ProjectStatusReport'),
  'ProjectStatusReport',
);
const ProjectVarianceReport = lazyNamed(
  () => import('@/pages/reports/ProjectVarianceReport'),
  'ProjectVarianceReport',
);
const ProjectTimeVarianceReport = lazyNamed(
  () => import('@/pages/reports/ProjectTimeVarianceReport'),
  'ProjectTimeVarianceReport',
);
const ProjectHistoryReport = lazyNamed(
  () => import('@/pages/reports/ProjectHistoryReport'),
  'ProjectHistoryReport',
);
// Phase 34c: cross-project reports
const CrossProjectRiskRegisterReport = lazyNamed(
  () => import('@/pages/reports/CrossProjectRiskRegisterReport'),
  'CrossProjectRiskRegisterReport',
);
const ChangeLogReport = lazyNamed(
  () => import('@/pages/reports/ChangeLogReport'),
  'ChangeLogReport',
);
const ResourcesUtilizationReport = lazyNamed(
  () => import('@/pages/reports/ResourcesUtilizationReport'),
  'ResourcesUtilizationReport',
);
const CapacityForecastReport = lazyNamed(
  () => import('@/pages/reports/CapacityForecastReport'),
  'CapacityForecastReport',
);
const SkillsCoverageReport = lazyNamed(
  () => import('@/pages/reports/SkillsCoverageReport'),
  'SkillsCoverageReport',
);
const UtilizationTrendReport = lazyNamed(
  () => import('@/pages/reports/UtilizationTrendReport'),
  'UtilizationTrendReport',
);
const CapExOpExReport = lazyNamed(
  () => import('@/pages/reports/CapExOpExReport'),
  'CapExOpExReport',
);
const ResourcesPortfolio = lazyNamed(
  () => import('@/pages/ResourcesPortfolio'),
  'ResourcesPortfolio',
);
const EstimatorPage = lazyNamed(() => import('@/pages/EstimatorPage'), 'EstimatorPage');
const MethodologyAdvisorPage = lazyNamed(() => import('@/pages/MethodologyAdvisorPage'), 'MethodologyAdvisorPage');
const TimesheetPage = lazyNamed(() => import('@/pages/TimesheetPage'), 'TimesheetPage');
const TeamTimePage = lazyNamed(() => import('@/pages/TeamTimePage'), 'TeamTimePage');
const MyTasksPage = lazyNamed(() => import('@/pages/MyTasksPage'), 'MyTasksPage');
const ComingSoonPage = lazyNamed<{ title: string }>(
  () => import('@/pages/ComingSoonPage'),
  'ComingSoonPage',
);
const PortfoliosPage = lazyNamed(() => import('@/pages/PortfoliosPage'), 'PortfoliosPage');
const PortfolioDetailPage = lazyNamed(
  () => import('@/pages/PortfolioDetailPage'),
  'PortfolioDetailPage',
);
const ProgramsPage = lazyNamed(() => import('@/pages/ProgramsPage'), 'ProgramsPage');
const ProgramDetailPage = lazyNamed(
  () => import('@/pages/ProgramDetailPage'),
  'ProgramDetailPage',
);
const CrossProjectSchedulePage = lazyNamed(
  () => import('@/pages/CrossProjectSchedulePage'),
  'CrossProjectSchedulePage',
);
const CrossProjectRisksPage = lazyNamed(
  () => import('@/pages/CrossProjectRisksPage'),
  'CrossProjectRisksPage',
);
const CrossProjectChangesPage = lazyNamed(
  () => import('@/pages/CrossProjectChangesPage'),
  'CrossProjectChangesPage',
);
const CrossProjectDocumentsPage = lazyNamed(
  () => import('@/pages/CrossProjectDocumentsPage'),
  'CrossProjectDocumentsPage',
);
const SettingsPage = lazyNamed(() => import('@/pages/SettingsPage'), 'SettingsPage');

export default function App() {
  return (
    <UpdateRequiredGate>
    <AuthProvider>
      <ChunkErrorBoundary>
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            {/* Auth routes — full-screen, no AppShell */}
            <Route path="/signup" element={<SignupPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ForgotPasswordPage />} />
            <Route path="/verify-email" element={<VerifyEmailPage />} />
            <Route path="/accept-invite" element={<AcceptInvitePage />} />

            {/* Application routes — wrapped in AppShell, optionally gated */}
            <Route
              path="/*"
              element={
                <AuthGate>
                  <AppShell>
                    {/* Inner Suspense: navigating between app routes swaps
                        chunks without unmounting the shell. */}
                    <Suspense fallback={<RouteFallback />}>
                      <Routes>
                        <Route path="/" element={<HomePage />} />
                        <Route path="/projects" element={<ProjectsPage />} />
                        <Route path="/intake" element={<IntakePage />} />
                        <Route path="/guide" element={<GuidePage />} />
                        <Route path="/whats-new" element={<WhatsNewPage />} />
                        <Route path="/lessons" element={<LessonsPage />} />
                        <Route path="/briefing" element={<BriefingPage />} />
                        <Route
                          path="/projects/:projectId"
                          element={<ProjectDetailPage />}
                        />

                        {/* Reports */}
                        <Route path="/reports" element={<ReportsHub />} />
                        <Route
                          path="/reports/portfolio-status"
                          element={<PortfolioStatusReport />}
                        />
                        <Route
                          path="/reports/benefits-realization"
                          element={<BenefitsRealizationReport />}
                        />
                        <Route
                          path="/reports/project-status/:projectId"
                          element={<ProjectStatusReport />}
                        />
                        <Route
                          path="/reports/variance/:projectId"
                          element={<ProjectVarianceReport />}
                        />
                        <Route
                          path="/reports/time-variance/:projectId"
                          element={<ProjectTimeVarianceReport />}
                        />
                        <Route
                          path="/reports/history/:projectId"
                          element={<ProjectHistoryReport />}
                        />
                        {/* Phase 34c: cross-project reports */}
                        <Route
                          path="/reports/cross-project-risks"
                          element={<CrossProjectRiskRegisterReport />}
                        />
                        <Route
                          path="/reports/change-log"
                          element={<ChangeLogReport />}
                        />
                        <Route
                          path="/reports/resources-utilization"
                          element={<ResourcesUtilizationReport />}
                        />
                        <Route
                          path="/reports/capacity-shortfall"
                          element={<CapacityForecastReport />}
                        />
                        <Route
                          path="/reports/skills-coverage"
                          element={<SkillsCoverageReport />}
                        />
                        <Route
                          path="/reports/utilization-trend"
                          element={<UtilizationTrendReport />}
                        />
                        <Route
                          path="/reports/capex-opex"
                          element={<CapExOpExReport />}
                        />

                        {/* Resources */}
                        <Route path="/resources" element={<ResourcesPortfolio />} />
                        <Route path="/estimator" element={<EstimatorPage />} />
                        <Route path="/methodology" element={<MethodologyAdvisorPage />} />
                        <Route path="/timesheet" element={<TimesheetPage />} />
                        <Route path="/my-tasks" element={<MyTasksPage />} />
                        <Route path="/team-time" element={<TeamTimePage />} />

                        {/* Portfolios (Phase 18) */}
                        <Route path="/portfolios" element={<PortfoliosPage />} />
                        <Route
                          path="/portfolios/:portfolioId"
                          element={<PortfolioDetailPage />}
                        />
                        <Route path="/programs" element={<ProgramsPage />} />
                        <Route
                          path="/programs/:programId"
                          element={<ProgramDetailPage />}
                        />

                        {/* Cross-project Schedule (Phase 19) */}
                        <Route
                          path="/schedule"
                          element={<CrossProjectSchedulePage />}
                        />

                        {/* Cross-project Risks (Phase 20) */}
                        <Route path="/risks" element={<CrossProjectRisksPage />} />

                        {/* Cross-project Changes / Documents */}
                        <Route
                          path="/changes"
                          element={<CrossProjectChangesPage />}
                        />
                        <Route
                          path="/documents"
                          element={<CrossProjectDocumentsPage />}
                        />
                        <Route path="/settings" element={<SettingsPage />} />
                        <Route
                          path="*"
                          element={<ComingSoonPage title="Not found" />}
                        />
                      </Routes>
                    </Suspense>
                  </AppShell>
                </AuthGate>
              }
            />
          </Routes>
        </Suspense>
      </ChunkErrorBoundary>
      <ToastViewport />
    </AuthProvider>
    </UpdateRequiredGate>
  );
}
